/**
 * Deciding the seating AFTER the four players have been entered.
 *
 * Normally the host types the players in seating order (first box = East, who
 * deals first) and `Game.players` is that order — dealer rotation
 * (`deriveTableState`) indexes into it, so the ORDER of `game.players` IS the
 * seating. The other option is "decide with the dice": the four names go in
 * in any order, the game starts with `seatingPending`, the host shares the
 * link and everyone rolls the dice, and only then does the host say who sits
 * at which wind. Nothing checks the dice — the roll never touches the game
 * (see `diceRoller.ts`); the host simply enters the result.
 *
 * Assigning the seats reorders `game.players` to East, South, West, North.
 * That is only safe while no round exists (every round's `dealer` and the
 * derived table state depend on the order), so it is refused once one does.
 * Player ids never change — only their position.
 */

import type { Wind } from '../rules/tiles';
import type { PlayerId } from '../rules/types';
import type { Game } from './types';

export const SEAT_WINDS: Wind[] = ['east', 'south', 'west', 'north'];

/** wind chosen for each player, keyed by player id */
export type SeatAssignment = Record<PlayerId, Wind>;

/** True while the host still has to say who sits where — no round can be recorded until then. */
export function isSeatingPending(game: Game): boolean {
  return game.seatingPending === true;
}

/**
 * The seating may be (re)assigned while it is pending, and afterwards for a
 * game that chose the dice option but hasn't recorded a round yet (in case
 * the host set it wrongly). Never once a round exists, and never for a game
 * whose seating was typed in at setup.
 */
export function canAssignSeating(game: Game): boolean {
  return game.seatingByDice === true && game.rounds.length === 0 && !game.endedAt;
}

/** A plain-English problem with `assignment`, or undefined if it is a valid seating. */
export function seatingProblem(game: Game, assignment: SeatAssignment): string | undefined {
  if (!canAssignSeating(game)) {
    return game.rounds.length > 0
      ? "The seating can't change once a round has been recorded."
      : 'This game has no seating to set.';
  }
  const missing = game.players.filter((p) => !assignment[p.id]);
  if (missing.length > 0) {
    return `Choose a seat for ${missing.map((p) => p.name).join(', ')}.`;
  }
  for (const p of game.players) {
    if (!SEAT_WINDS.includes(assignment[p.id]!)) return `${p.name} has no valid seat.`;
  }
  for (const wind of SEAT_WINDS) {
    const holders = game.players.filter((p) => assignment[p.id] === wind);
    if (holders.length > 1) {
      return `${label(wind)} is chosen for ${holders.map((p) => p.name).join(' and ')} — each seat can only be used once.`;
    }
  }
  return undefined;
}

function label(wind: Wind): string {
  return wind.charAt(0).toUpperCase() + wind.slice(1);
}

/**
 * Puts `game.players` into East, South, West, North order, records each
 * player's starting `seat`, and clears `seatingPending`. Throws (with the
 * `seatingProblem` text) if the assignment isn't valid — the UI validates
 * first, this is the backstop.
 */
export function applySeating(game: Game, assignment: SeatAssignment): void {
  const problem = seatingProblem(game, assignment);
  if (problem) throw new Error(problem);
  const byWind = (wind: Wind) => game.players.find((p) => assignment[p.id] === wind)!;
  game.players = SEAT_WINDS.map((wind) => ({ ...byWind(wind), seat: wind }));
  game.seatingPending = false;
}
